import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Base, IconNames } from '../../../common';
import { AutoUnsubscribe } from '../../../common/decorators/auto-unsubscribe.decorator';
import { UserRolesModel } from '../../../store';
import {
  chipsInputOptionValueMap,
  DraggableFilterType,
  FilterState,
  RecordTypeFilterKey,
  recordTypeFilterMap,
  RecordTypeFilterOptionValueKey,
} from '../../../store/constants';
import { FilterName } from '../../../store/models/filter.model';
import { ButtonAction, ButtonSize, ButtonType } from '../../action-button/constants';
import { MonitoringFiltersFieldComponent } from './filters-field/monitoring-filters-field.component';
import { MonitoringPageService } from './page/monitoring-page.service';
import { MonitoringSelectProfileComponent } from './select-profile/monitoring-select-profile.component';

@Component({
  selector: 'ui-monitoring-filters',
  templateUrl: './monitoring-filters.component.html',
  styleUrls: ['./monitoring-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class MonitoringFiltersComponent {
  @Input() filtersForm: UntypedFormGroup;
  @Input() selectedProfileForm: UntypedFormGroup;
  @Input() isCustomizedMode: boolean;
  @Input() isFiltersCustomizable = true;
  @Input() enabledFilters: FilterName[];
  @Input() disabledFilters: FilterName[];
  @Input() filterTypeByName: Map<FilterName, DraggableFilterType>;
  @Input() filterSelectOptionsByName: Map<FilterName, string[] | Base[]>;
  @Input() canCreateOrUpdateProfile: boolean;
  @Input() isUpdateProfileAvailable: boolean;
  @Input() isProfileVisible = true;
  @Input() filtersCount = 0;
  @Input() defaultProfileControl?: FormControl<boolean>;
  @Input() filterRadioOptionsByName?: Map<FilterName, string[] | UserRolesModel>;
  @Input() modalBoxTitle: string;
  @Input() isDialogBox = false;
  @Output() removeProfile = new EventEmitter<void>();
  @Output() selectProfile = new EventEmitter<void>();
  @Output() updateProfile = new EventEmitter<void>();
  @Output() enableCustomizeMode = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<void>();
  @Output() resetFilters = new EventEmitter<void>();
  @Output() openCreateProfileDialog = new EventEmitter<void>();
  @Output() updateFilterSettings = new EventEmitter<void>();
  @Output() cancelUpdatingFilterSettings = new EventEmitter<void>();
  buttonAction = ButtonAction;
  buttonSize = ButtonSize;
  buttonType = ButtonType;
  transportFiltersState = FilterState;
  subscriptions: Subscription[] = [];
  chipsInputOptionValueMap: Map<string, RecordTypeFilterOptionValueKey[]> = chipsInputOptionValueMap;
  recordTypeFilterMap: Map<string, RecordTypeFilterKey[]> = recordTypeFilterMap;
  iconNameList = IconNames;
  @ViewChildren(MonitoringFiltersFieldComponent)
  monitoringFiltersFieldComponent: QueryList<MonitoringFiltersFieldComponent>;
  @ViewChild(MonitoringSelectProfileComponent)
  monitoringSelectProfileComponent: MonitoringSelectProfileComponent;

  settingsChanged = false;

  constructor(private monitoringPageService: MonitoringPageService) {}

  @HostListener('keydown.enter', ['$event'])
  onEnterKey(event: KeyboardEvent): void {
    if (!this.isCustomizedMode && this.filtersForm.pristine) {
      return;
    }
    if (this.isCustomizedMode && !this.settingsChanged) {
      return;
    }
    if (this.isCustomizedMode) {
      this.updateFilterSettings.emit();
    } else {
      this.apply(true);
    }
    event.preventDefault();
  }

  markForCheck(): void {
    this.monitoringFiltersFieldComponent.forEach((component: MonitoringFiltersFieldComponent) =>
      component.cd.markForCheck(),
    );
  }

  apply(isClosePrevented?: boolean): void {
    if (this.monitoringPageService.matDrawer$.getValue() && !isClosePrevented) {
      this.monitoringPageService.close();
    }

    this.applyFilters.emit();
  }

  close(): void {
    this.monitoringPageService.close();
  }

  reset(): void {
    this.selectedProfileForm?.reset();
    this.selectedProfileForm?.updateValueAndValidity();
    this.resetFilters.emit();
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.settingsChanged = true;
    this.markForCheck();
  }

  changeFieldState(filterName: FilterName, targetState: FilterState): void {
    let currentContainer: FilterName[];
    let targetContainer: FilterName[];

    if (targetState === FilterState.Enabled) {
      currentContainer = this.disabledFilters;
      targetContainer = this.enabledFilters;
    } else {
      currentContainer = this.enabledFilters;
      targetContainer = this.disabledFilters;
    }

    const currentIndex = currentContainer.indexOf(filterName);
    const targetIndex = targetContainer.length;

    transferArrayItem(currentContainer, targetContainer, currentIndex, targetIndex);

    this.settingsChanged = true;
    this.markForCheck();
  }
}
